export const setRuleStyle = (editor, selector, style) => {
  const css = editor.CssComposer;

  const rule = css.getRule(selector);

  if (rule) {
    const ruleStyle = rule.get('style');
    rule.set('style', { ...ruleStyle, ...style });
  } else {
    css.setRule(selector, style);
  }
};

/**
 *
 * @param {object} selected Selected component provided by grapesjs
 * @returns {string} Returns src attribute of the image or background-image property
 */
export const getSelectedImageUrl = (selected) => {
  if (!selected) {
    return '';
  }

  const type = selected.get('type');

  if (type === 'image') {
    return selected.get('src');
  }

  if (type === 'icon-wrapper') {
    return selected.get('src') || selected.getStyle()['background-image'] || '';
  }

  return selected.getStyle()['background-image'] || '';
};

/**
 * An utility function to help determine if the selected is a valid anchor node
 * @param {object} rte Grapesjs rich text editor object
 */
export const isValidAnchor = (rte) => {
  const anchor = rte.selection().anchorNode;

  if (!anchor) {
    return false;
  }

  const { parentNode, nextSibling } = anchor;

  return parentNode?.nodeName === 'A' || nextSibling?.nodeName === 'A';
};

export const withWrapper = (component) => {
  return {
    type: 'div-wrapper',
    tagName: 'div',
    attributes: {
      class: 'default-wrapper',
    },
    style: {
      width: '100%',
    },
    selectable: true,
    highlightable: true,
    droppable: false,
    editable: false,
    components: [component],
  };
};

export const replaceDraggedComponentWithWrapper = (editor, selectedItem) => {
  const selectedItemType = selectedItem.get('type');

  const wrappedBlock = withWrapper(selectedItem.clone());
  const replacedBlock = selectedItem.replaceWith(wrappedBlock);

  editor.select(replacedBlock.findType(selectedItemType));
  return replacedBlock;
};

/**
 * Tells whether an image component should be auto-wrapped in a `default-wrapper` div.
 * Bare images are wrapped, while those in `<a>` or `<figure>` tags are skipped to preserve link/caption structures.
 */
export const shouldWrapImageInDefaultWrapper = (imageComponent) => {
  const parent = imageComponent.parent();

  if (!parent) {
    return false;
  }

  return (
    parent.get('type') !== 'div-wrapper' &&
    parent.get('tagName') !== 'a' &&
    parent.get('tagName') !== 'figure'
  );
};

/**
 * Normalize a page-width value to a bare px number string (e.g. "600").
 * Windows Outlook (Word HTML engine) ignores CSS max-width; width must also be an HTML attribute.
 */
export const normalizePageWidthPx = (widthPx, fallback = '600') => {
  const raw = String(widthPx ?? fallback).trim();
  const match = raw.match(/(\d+)/);
  return match ? match[1] : String(fallback).replace(/px$/i, '');
};

/**
 * Constrain the email page wrapper so Page Settings width survives Windows Outlook.
 * Sets HTML width + align attributes and explicit style width/max-width (not width:100%).
 */
export const applyOutlookSafePageWidth = (pageWrapper, widthPx) => {
  if (!pageWrapper) return;

  const width = normalizePageWidthPx(widthPx);

  if (typeof pageWrapper.addAttributes === 'function') {
    pageWrapper.addAttributes({ width, align: 'center' });
  }

  if (typeof pageWrapper.addStyle === 'function') {
    pageWrapper.addStyle({
      width: `${width}px`,
      'max-width': `${width}px`,
    });
  } else if (typeof pageWrapper.setStyle === 'function') {
    pageWrapper.setStyle({
      ...(typeof pageWrapper.getStyle === 'function'
        ? pageWrapper.getStyle()
        : {}),
      width: `${width}px`,
      'max-width': `${width}px`,
    });
  }
};

/**
 * Read the effective page-wrapper width from style or HTML attributes.
 */
export const readPageWrapperWidth = (pageWrapper, fallback = '700') => {
  if (!pageWrapper) return String(fallback);

  const style =
    (typeof pageWrapper.getStyle === 'function' && pageWrapper.getStyle()) ||
    {};
  const attrs =
    (typeof pageWrapper.getAttributes === 'function' &&
      pageWrapper.getAttributes()) ||
    {};

  const styleWidth = style.width && style.width !== '100%' ? style.width : null;

  return normalizePageWidthPx(
    style['max-width'] || styleWidth || attrs.width || fallback,
    fallback,
  );
};

/**
 * Build the root page-wrapper table HTML with Outlook-safe width attributes.
 */
export const buildRootPageWrapperHtml = ({
  widthPx,
  paddingPx = 20,
  bgColor = '#ffffff',
  cellInner = '',
}) => {
  const width = normalizePageWidthPx(widthPx, '700');

  return `
      <table
        data-type="root"
        width="${width}"
        align="center"
        style="
          width: ${width}px;
          max-width: ${width}px;
          padding: 0 ${paddingPx}px;
          height: 100%;
          margin: 0 auto;
          background-color: ${bgColor};
        ">
        <tbody data-type="root-body">
          <tr data-type="root-row">
            <td
              data-type="root-cell"
              style="vertical-align: top;
              width: 100%; height: 100%;"
            >${cellInner}</td>
          </tr>
        </tbody>
      </table>
    `;
};
